'use client'
import { useEffect, useRef, useState } from 'react'
import styles from './Select.module.scss'
import { classNames } from '@/src/helpers/component'
import SVGFlatTriangle from '@/src/components/common/SVG/SVGFlatTriangle'

export type SelectOptionProps =
  | string
  | {
      display: string
      value: never
    }

export type SelectProps = {
  required?: boolean
  name?: string
  className?: string
  options: SelectOptionProps[]
  default?: number | null
  placeholder?: string
  nullable?: boolean
  onChange?: (index: number | null) => void
  valueSerializer?: (arg0: never) => string
  //TODO: maybe in the future: [searchable?: boolean]
}

export default function Select({ placeholder = 'Select option', required, ..._ }: SelectProps) {
  const [selectedIdx, setSelectedIdx] = useState(_.default ?? null)
  const [opened, setOpened] = useState(false)
  const refInput = useRef<HTMLInputElement>(null)

  const handleChange = (i: number | null) => {
    setSelectedIdx(i)
    setOpened(false)
    _.onChange?.(i)
  }

  useEffect(() => {
    refInput.current?.dispatchEvent(new Event('change', { bubbles: true }))
  }, [selectedIdx])

  const selectedOption = _.options[selectedIdx ?? -1] ?? null
  const isPlaceholder = selectedOption === null

  let selectedDisplay = selectedOption
  if (selectedDisplay !== null && typeof selectedDisplay !== 'string')
    selectedDisplay = selectedDisplay.display

  let selectedValue: SelectOptionProps | null = _.options[selectedIdx ?? -1] ?? null
  if (selectedValue !== null && typeof selectedValue !== 'string')
    selectedValue = selectedValue.value
  if (selectedValue !== null && typeof selectedValue !== 'string')
    selectedValue = _.valueSerializer?.(selectedValue) ?? null

  return (
    <>
      <input
        ref={refInput}
        name={_.name}
        required={required}
        type={'text'}
        value={selectedValue ?? ''}
        style={{ display: 'none', opacity: 0, visibility: 'hidden' }}
        tabIndex={-1}
        onChange={() => {}}
      />
      <div
        tabIndex={0}
        className={classNames(styles.container, opened ? styles.open : '', _.className)}
        onClick={e => {
          e.preventDefault()
          setOpened(!opened)
        }}
        onBlur={() => setOpened(false)}>
        <div className={classNames(styles.display, isPlaceholder ? styles.placeholder : '')}>
          {isPlaceholder ? placeholder : selectedDisplay}
          <SVGFlatTriangle
            direction={opened ? 'up' : 'down'}
            className={classNames(styles.transition, styles.arrow)}
          />
        </div>
        {opened && (
          <div className={styles.options}>
            {_.options.map((_, i) => (
              <div key={i} onClick={() => handleChange(i)} className={styles.option}>
                {typeof _ === 'string' ? _ : _.display}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
